import * as React from 'react'

import { Box, Button, Icon, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  CustomizationField,
  fieldLabel,
  typeLabel,
  useExpensesCustomizationUpdate,
} from 'modules/expenses/hooks/useExpensesCustomization'

import useFieldState from './useFieldState'

interface Props {
  field: CustomizationField
  withDragAndDropIcon?: boolean
  onClickItemSettings?: (field: CustomizationField) => void
}

const getDisabledAriaLabel = (field: string) => {
  return i18n.t('expenses.settings.customization.helpers.disable_field', { field })
}

const getEnabledAriaLabel = (field: string) => {
  return i18n.t('expenses.settings.customization.helpers.enable_field', { field })
}

const SingleItem = ({
  field,
  withDragAndDropIcon,
  onClickItemSettings = () => undefined,
}: Props) => {
  const subtitleLabel = field.mandatory
    ? i18n.t('expenses_v2.default_fields.mandatory')
    : i18n.t('expenses_v2.default_fields.optional')

  const { enabled, canChangeEnabled, toggleEnableLocally } = useFieldState(field)

  const handleFieldEnabled = async () => {
    toggleEnableLocally(async (_, newEnabled) => {
      const response = await updateField({
        companyId: currentCompany.id,
        id: field.id,
        enabled: newEnabled,
      })

      if (response.errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('expenses.settings.customization.update.error'),
        })
      } else {
        app.setFlash({
          type: 'correct',
          message: i18n.t('expenses.settings.customization.update.success'),
        })
      }
    })
  }

  const { company: currentCompany } = useCurrentSession()

  const onClick = () => onClickItemSettings(field)
  const { updateField } = useExpensesCustomizationUpdate()
  const label = fieldLabel(field)

  return (
    <Box
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      width="full"
      aria-label={i18n.t('expenses.settings.customization.helpers.field', { field: label })}
    >
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        overflowX="hidden"
        width="full"
      >
        <Box overflowX="hidden" paddingRight="s16" gap="s4">
          {withDragAndDropIcon && <Icon.Medium icon="drag-and-drop" />}
          <Text size="300" weight="medium">
            {label}
          </Text>
          <Text size="300" weight="medium" color="grey600">
            {subtitleLabel}
          </Text>
          <Text size="200" weight="medium" color="grey600">
            {typeLabel(field)}
          </Text>
        </Box>
        <Box gap="s16">
          <TogglerCheckbox
            disabled={!canChangeEnabled}
            value={enabled}
            onChange={handleFieldEnabled}
            aria-label={enabled ? getDisabledAriaLabel(label) : getEnabledAriaLabel(label)}
          />
          <Button
            type="secondary"
            onClick={onClick}
            aria-label={i18n.t('expenses.settings.customization.helpers.field_settings', {
              field: label,
            })}
            iconName="settings"
          />
        </Box>
      </Box>
    </Box>
  )
}

export default observer(SingleItem)
